import { formatSimpleList, validateField } from './helpers';

export const codeTypeMap = {
  0: 'maintenanceMode',
  1: 'operationalStopMode',
  2: 'effectiveMode',
};

// Funções utilitárias para montar os dados
const sortEquipmentByType = (equipments) => {
  const order = { 2: 0, 1: 1, 0: 2 };
  return [...equipments].sort(
    (a, b) => (order[a.code_type] ?? 999) - (order[b.code_type] ?? 999),
  );
};

const sortEquipmentAlphabetically = equipments => [...equipments].sort((a, b) => {
  const labelA = String(a?.equip_name ?? '').trim();
  const labelB = String(b?.equip_name ?? '').trim();

  return labelA.localeCompare(labelB, undefined, {
    numeric: true,
    sensitivity: 'base',
    ignorePunctuation: true,
  });
});

const sortEquipmentByCount = (groupedEquipmentTypes) => {
  const dataWithCount = groupedEquipmentTypes.map((type) => {
    let equipmentCountTotal = 0;

    type.list.forEach((group) => {
      equipmentCountTotal += group.list ? group.list.length : 0;
    });

    return {
      ...type,
      equipmentCountTotal,
    };
  });
  dataWithCount.sort((a, b) => b.equipmentCountTotal - a.equipmentCountTotal);
  return dataWithCount;
};

export const getEquipmentStatusCounter = (groupList) => {
  const initialCounter = {
    effectiveMode: 0,
    operationalStopMode: 0,
    maintenanceMode: 0,
  };

  if (!groupList || groupList.length === 0) {
    return initialCounter;
  }

  return groupList.reduce((acc, item) => {
    const key = codeTypeMap[item.code_type];
    if (key) acc[key] += 1;
    return acc;
  }, initialCounter);
};

const getPreviousGroup = (prevData, currentType, currentGroup) => {
  const previousEquipType = prevData.find(
    prevType => prevType.equip_type === currentType.equip_type,
  );

  if (!previousEquipType) {
    return null;
  }

  return previousEquipType.list.find(
    prevGroup => prevGroup.equip_group_id === currentGroup.equip_group_id,
  );
};

// Funções para montagem dos ícones de equipamentos
const ICON_STYLE = {
  defaultStyle: { height: '33.75px', width: '37.5px' },
  exceptionStyle: { height: '19px', width: '37.5px' },
};

const createDefaultIconConfig = iconName => ({
  style: ICON_STYLE.defaultStyle,
  iconName,
});

const ALL_ICON_CONFIGS = {
  truckEmpty: createDefaultIconConfig('truckEmptyV2'),
  truckFull: createDefaultIconConfig('truckFullV2'),
  truckUnloading: createDefaultIconConfig('truckUnloading'),
  excavator: createDefaultIconConfig('excavator'),
  drill: createDefaultIconConfig('drill'),
  infraLoad: createDefaultIconConfig('infraLoad'),
  tractorOriginal: createDefaultIconConfig('tractorOriginal'),
  motorGrader: createDefaultIconConfig('motorGraderV2'),
  waterTruck: createDefaultIconConfig('waterTruck'),
  bobcat: createDefaultIconConfig('bobcat'),
  supportTruck: createDefaultIconConfig('supportTruck'),
  jumbo: createDefaultIconConfig('jumbo'),
  scaler: createDefaultIconConfig('scaler'),
  tractor: { style: ICON_STYLE.exceptionStyle, iconName: 'tractor' },
  supply: createDefaultIconConfig('supply'),
  loader: createDefaultIconConfig('loaderV2'),
  fandrill: createDefaultIconConfig('fandrill'),
  cabolt: createDefaultIconConfig('cabolt'),
  rockBolt: createDefaultIconConfig('rockBolt'),
  raiseBoring: createDefaultIconConfig('raiseBoring'),
  shaft: createDefaultIconConfig('shaft'),
  concreteMixer: createDefaultIconConfig('concreteMixer'),
  designedRobot: createDefaultIconConfig('designedRobot'),
  pasteFill: createDefaultIconConfig('pasteFill'),
  liftingPlataform: createDefaultIconConfig('liftingPlataform'),
  crusher: createDefaultIconConfig('crusher'),
  breaker: createDefaultIconConfig('breaker'),
  steamRoller: createDefaultIconConfig('steamRoller'),
  emulsionTruck: createDefaultIconConfig('emulsionTruck'),
  rockFill: createDefaultIconConfig('rockFill'),
  lightVehicles: createDefaultIconConfig('lightVehicles'),
  locomotive: createDefaultIconConfig('locomotive'),
  wagon: createDefaultIconConfig('wagon'),
};

const BASE_ICON_NAME_MAP = {
  1: 'excavator',
  2: 'truckEmpty',
  4: 'drill',
  5: 'infraLoad',
  6: 'tractorOriginal',
  7: 'motorGrader',
  8: 'waterTruck',
  9: 'bobcat',
  10: 'supportTruck',
  11: 'jumbo',
  12: 'scaler',
  13: 'tractor',
  14: 'supply',
  15: 'loader',
  16: 'fandrill',
  17: 'cabolt',
  18: 'rockBolt',
  19: 'raiseBoring',
  20: 'shaft',
  21: 'concreteMixer',
  22: 'designedRobot',
  23: 'pasteFill',
  24: 'liftingPlataform',
  25: 'crusher',
  26: 'breaker',
  27: 'steamRoller',
  28: 'emulsionTruck',
  29: 'rockFill',
  30: 'lightVehicles',
  31: 'locomotive',
  32: 'wagon',
};

const getEquipmentIconNameBase = equipType => BASE_ICON_NAME_MAP[equipType] || 'excavator';

const getTypeMode = item => codeTypeMap[item?.code_type] || 'inactiveMode';

const formatItemData = (item) => {
  const baseIconName = getEquipmentIconNameBase(item.equip_type);
  const iconBaseConfig = ALL_ICON_CONFIGS[baseIconName] || ALL_ICON_CONFIGS.truckEmpty;

  const codeTypeMode = getTypeMode(item);

  return {
    ...item,
    icon: {
      ...iconBaseConfig.style,
      iconName: iconBaseConfig.iconName,
      typeMode: codeTypeMode,
    },
  };
};

const groupDataByHierarchy = (list) => {
  const equipmentTypesMap = new Map();
  const equipmentGroupsMap = new Map();

  return list.reduce((acc, item) => {
    let equipType = equipmentTypesMap.get(item.equip_type);

    if (!equipType) {
      equipType = {
        label: item?.equip_type_name,
        equip_type: item.equip_type,
        list: [],
      };
      equipmentTypesMap.set(item.equip_type, equipType);
      acc.push(equipType);
    }

    const groupKey = `${item.equip_type}-${item.id_equip_group}`;
    let equipGroup = equipmentGroupsMap.get(groupKey);

    if (!equipGroup) {
      equipGroup = {
        label: item?.equip_group_name,
        equip_group_id: item.id_equip_group,
        list: [],
      };
      equipmentGroupsMap.set(groupKey, equipGroup);
      equipType.list.push(equipGroup);
    }
    const formatetedEquipItem = formatItemData(item);

    equipGroup.list.push(formatetedEquipItem);

    return acc;
  }, []);
};

const processGroupsAndCheckCounter = (data, previousMountedData, shouldSortAlphabetically) => {
  const dataWithCounters = data.map(equipType => ({
    ...equipType,
    list: equipType.list.map((equipGroup) => {
      const sortedList = shouldSortAlphabetically
        ? sortEquipmentAlphabetically(equipGroup.list)
        : sortEquipmentByType(equipGroup.list);

      const newCounter = getEquipmentStatusCounter(sortedList);
      const previousGroup = getPreviousGroup(
        previousMountedData,
        equipType,
        equipGroup,
      );

      if (
        previousGroup
        && previousGroup.counter
        && newCounter.effectiveMode === previousGroup.counter.effectiveMode
        && newCounter.operationalStopMode
          === previousGroup.counter.operationalStopMode
        && newCounter.maintenanceMode === previousGroup.counter.maintenanceMode
      ) {
        return {
          ...equipGroup,
          list: sortedList,
          counter: previousGroup.counter,
        };
      }
      return {
        ...equipGroup,
        list: sortedList,
        counter: newCounter,
      };
    }),
  }));
  return dataWithCounters;
};

export const mountData = (
  list,
  previousMountedData = [],
  sortEquipmentStatusFlag,
) => {
  const groupedData = groupDataByHierarchy(list);
  const shouldSortAlphabetically = sortEquipmentStatusFlag === '1';

  const sortedList = sortEquipmentByCount(groupedData);

  const dataWithCounters = processGroupsAndCheckCounter(
    sortedList,
    previousMountedData,
    shouldSortAlphabetically,
  );
  return dataWithCounters;
};

export const filterEquipments = (data, currentFilter) => data
  .map((equip) => {
    const isEquipTypeValid = validateField(
      currentFilter.equip_type,
      equip.equip_type,
    );

    if (!isEquipTypeValid) return null;

    const filterEquipGroup = equip.list
      .map((group) => {
        const isGroupValid = validateField(
          currentFilter.equip_group_id,
          group.equip_group_id,
        );

        if (!isGroupValid) return null;

        const filterEquipItem = group.list.filter(
          item => validateField(currentFilter.equip_id, item.id_equip)
              && validateField(currentFilter.code_type, item.code_type)
              && validateField(currentFilter.code_group_id, item.id_code_group)
              && validateField(currentFilter.code_name, item.code_name)
              && validateField(currentFilter.element_id, item.element_id)
              && validateField(currentFilter.point_id, item.point_id),
        );

        if (filterEquipItem.length === 0) return null;

        return {
          ...group,
          list: filterEquipItem,
        };
      })
      .filter(Boolean);
    if (filterEquipGroup.length === 0) return null;

    return { ...equip, list: filterEquipGroup };
  })
  .filter(Boolean);


// Funções para montar opções de menu de filtros
const filterEquipmentGroup = (list, filters) => {
  if (!list || list.length === 0) return [];

  return list.filter(equip => validateField(filters.equip_type, equip.id_equipament));
};

const filterEquipment = (list, filters) => {
  if (!list || list.length === 0) return [];

  return list.filter((equip) => {
    const matchType = validateField(filters.equip_type, equip.id_equip);
    const matchGroup = validateField(filters.equip_group_id, equip.id_group);
    return matchType && matchGroup;
  });
};

const filterSubElement = (list, filters) => {
  if (!list || list.length === 0) return [];

  return list.filter(item => validateField(filters.element_id, item.id_element));
};

const filterCodeGroups = (list, filters) => {
  if (!list || list.length === 0) return [];

  const uniqueCodeGroups = new Map();
  const filteredGroups = list.filter((item) => {
    const matchCodeType = validateField(filters.code_type, item.code_type);
    const matchEquip = validateField(filters.equip_type, item.id_equip);
    return matchCodeType && matchEquip;
  });
  filteredGroups.forEach((item) => {
    const groupId = item?.id;
    const groupName = item?.name;
    if (groupId && !uniqueCodeGroups.has(groupId)) {
      uniqueCodeGroups.set(groupId, { id: groupId, name: groupName });
    }
  });
  return Array.from(uniqueCodeGroups.values());
};

const filterCodes = (list, filters) => {
  if (!list || list.length === 0) return [];

  const uniqueCodes = new Map();

  const filteredCodes = list.filter((item) => {
    const matchCodeType = validateField(filters.code_type, item.code_type);
    const matchCodeGroup = validateField(filters.code_group_id, item.id_group);
    const matchEquip = validateField(filters.equip_type, item.id_equip);

    return matchCodeType && matchCodeGroup && matchEquip;
  });

  filteredCodes.forEach((item) => {
    const codeName = item?.name;

    if (codeName && !uniqueCodes.has(codeName)) {
      uniqueCodes.set(codeName, { name: codeName });
    }
  });
  return Array.from(uniqueCodes.values());
};

export const formatFilterOptions = ({
  equipmentTypes = [],
  equipmentsGroups = [],
  equipments = [],
  elements = [],
  subElements = [],
  codes = [],
  codeTypes = [],
  codeGroups = [],
  filters = {},
}) => {
  const equipmentType = formatSimpleList(equipmentTypes);

  const equipmentGroupFiltered = filterEquipmentGroup(
    equipmentsGroups,
    filters,
  );
  const equipmentGroup = formatSimpleList(equipmentGroupFiltered);

  const equipmentFiltered = filterEquipment(equipments, filters);
  const equipment = formatSimpleList(equipmentFiltered);

  const element = formatSimpleList(elements);

  const subElementFiltered = filterSubElement(subElements, filters);
  const subElement = formatSimpleList(subElementFiltered);

  const codeType = formatSimpleList(codeTypes);

  const codeGroupsFiltered = filterCodeGroups(codeGroups, filters);
  const codeGroup = formatSimpleList(codeGroupsFiltered);

  const codesFiltered = filterCodes(codes, filters);
  const code = formatSimpleList(codesFiltered, 'name');

  return {
    equipmentType,
    equipmentGroup,
    equipment,
    element,
    subElement,
    codeType,
    codeGroup,
    code,
  };
};

export const getFieldDependents = (formFields) => {
  const dependentsKeys = formFields
    .filter(field => field?.dependsOn)
    .map(field => field.key);
  return dependentsKeys;
};

export const getFormFields = (formattedData, translate) => {
  const {
    equipmentType,
    equipmentGroup,
    equipment,
    element,
    subElement,
    codeType,
    codeGroup,
    code,
  } = formattedData;

  return [
    {
      key: 'equip_type',
      options: equipmentType,
      component: 'multiSelect',
      description: translate('common:EquipmentType'),
    },
    {
      key: 'equip_group_id',
      options: equipmentGroup,
      component: 'multiSelect',
      description: translate('common:EquipmentGroup'),
    },
    {
      key: 'equip_id',
      options: equipment,
      component: 'multiSelect',
      description: translate('common:Equipament'),
    },
    {
      key: 'element_id',
      options: element,
      component: 'multiSelect',
      description: translate('common:Element'),
    },
    {
      key: 'point_id',
      options: subElement,
      component: 'multiSelect',
      description: translate('common:SubElement'),
    },
    {
      key: 'code_type',
      options: codeType,
      component: 'multiSelect',
      description: translate('common:CodeType'),
    },
    {
      key: 'code_group_id',
      options: codeGroup,
      component: 'multiSelect',
      description: translate('common:CodeGroup'),
    },
    {
      key: 'code_name',
      options: code,
      component: 'multiSelect',
      description: translate('common:Code'),
    },
  ];
};
